///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { isBefore } from "date-fns";

import Modal from "../../Modal";
import CustomDatePicker from "../../form-controls/CustomDatePicker";
import DocumentUploadModal from "../summary/DocumentUploadModal";
import { openNextRoundForBid } from "../../../api/bids";
import {
  fetchBidSummary,
  fetchBidMessages
} from "../../../slices/bidSummarySlice";
import { fetchBidDetails } from "../../../slices/bidDetailsSlice";
import messages from "../../../helpers/messages";
import { checkLaneRepriceInProgress } from "../../../slices/bidPricingSlice";

function OpenNextRoundModal({ open, summary, role, handleModalClose }) {
  const dispatch = useDispatch();
  const route = useLocation().pathname.split("/")[3] || "";
  const { bidId, closeDateUTC, isCoyoteBid } = summary;

  const [newRfqCloseDate, setNewRfqCloseDate] = useState(closeDateUTC);
  const [openDocumentUploadModal, setOpenDocumentUploadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleOpenNextRound = () => {
    setIsLoading(true);
    setIsError(false);
    const openNextRoundRequest = {
      nextRoundCloseDateUTC: newRfqCloseDate
    };
    openNextRoundForBid(bidId, openNextRoundRequest)
      .then(() => {
        setIsSuccess(true);
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  };

  const closeDocumentUploadModal = nextRoundOpened => {
    setIsError(false);
    setIsSuccess(false);
    if (nextRoundOpened) {
      dispatch(checkLaneRepriceInProgress(bidId));
      dispatch(fetchBidSummary({ bidId, role }));
      route === "summary" && dispatch(fetchBidMessages(bidId));
      route === "details" && dispatch(fetchBidDetails(bidId));
    }
    handleModalClose();
  };

  return (
    <>
      <Modal
        title={messages.openNextRound.defaultMessage}
        open={open && !openDocumentUploadModal}
        large
        handleClose={handleModalClose}
        titleClass="modal__title_light uppercase"
        bodyClass="border-top-grey px-24 pt-24"
        closeButtonClass="modal__close-button mr-16 mt-16"
        modalStyle={{ width: "498px" }}
      >
        <p
          data-testid="select-rfq-close-date"
          className="font-size-16 text-dim-grey mb-16 px-4"
        >
          {
            !isCoyoteBid ?
            messages.updateThePricingDueDateWithTheNextRoundDueDate
              .defaultMessage :
            messages.updateTheRfqCloseDateWithTheNextRoundCloseDate
              .defaultMessage
          }
        </p>
        <div className="grid">
          <div className="grid__cell one-whole pos-relative p-4">
            <CustomDatePicker
              id="RFQ Close Date"
              value={newRfqCloseDate}
              label={!isCoyoteBid ? messages.pricingDueDateWithoutDate.defaultMessage : messages.rfqCloseDateWithoutDate.defaultMessage}
              minDate={new Date()}
              onChange={date => setNewRfqCloseDate(date)}
            />
          </div>
          <div className="grid__cell mb-24 mr-4 fr">
            <button
              data-testid="next-button"
              className="primary-button one-whole cursor-pointer univers-67-bold-cond short-button"
              disabled={isBefore(
                new Date(newRfqCloseDate).setHours(0, 0, 0, 0),
                new Date().setHours(0, 0, 0, 0)
              )}
              onClick={() => setOpenDocumentUploadModal(true)}
            >
              {messages.next.defaultMessage}
            </button>
          </div>
        </div>
      </Modal>
      {openDocumentUploadModal && (
        <DocumentUploadModal
          bidId={bidId}
          handleClose={closeDocumentUploadModal}
          modalTitle={messages.openNextRound.defaultMessage}
          modalContent={
            messages.uploadTheFeedbackReceivedFromTheShipper.defaultMessage
          }
          checkboxContent={
            messages.openNextRoundModalCheckboxMessage.defaultMessage
          }
          onSuccess={handleOpenNextRound}
          isLoading={isLoading}
          isSuccess={isSuccess}
          isError={isError}
          alertTitle={messages.uploadComplete.defaultMessage}
          alertMessage={
            messages.theNextRoundHasBeenSuccessfullyOpened.defaultMessage
          }
          alertModalLarge={true}
          alertModalStyle={{ minHeight: "178px" }}
        />
      )}
    </>
  );
}

export default OpenNextRoundModal;
